/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/adapter/client/workspace.client.adapter.ts
 *
 * Client-side workspace adapter that syncs between SQLite and common item format
 * This file contains only the client adapter, no Prisma dependencies
 */

import type { UserAuth } from '@repo/common';
import type { AdapterConfig } from '@repo/common/types/Adapter';
import type { BaseModel as BaseModelType } from '@repo/common/types/Sqlite';
import type { Workspace } from '@repo/common/types/Workspace';
import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { CommonItem, DeduplicationStrategy } from '../types';

// ─── COMMON ITEM TYPE DEFINITION ───

export interface WorkspaceCommonItem extends CommonItem {
  id: string;
  name: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  data?: unknown; // Parsed JSON data
}

// Define the SQLite row type locally (matches workspace.lite.ts)
export type WorkspaceRow = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  data?: string; // JSON string
};

// ─── CLIENT ADAPTER IMPLEMENTATION ───

export class WorkspaceClientAdapter extends PrivateClientDatabaseAdapter<
  WorkspaceRow,
  WorkspaceCommonItem
> {
  constructor(
    sqliteModel: BaseModelType<Workspace, WorkspaceRow>,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: AdapterConfig
  ) {
    super(sqliteModel, 'workspace', userId, authToken, adapterConfig);
  }

  // ─── ABSTRACT METHOD IMPLEMENTATIONS ───

  protected sqliteRowToItem(row: WorkspaceRow): WorkspaceCommonItem {
    return {
      id: row.id,
      name: row.name,
      userId: this.userId,
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined,
      data: row.data ? parse(row.data) : undefined
    };
  }

  protected itemToSqliteRow(item: WorkspaceCommonItem): WorkspaceRow {
    return {
      id: item.id,
      name: item.name,
      createdAt: item.createdAt.toISOString(),
      updatedAt: item.updatedAt.toISOString(),
      deletedAt: item.deletedAt?.toISOString(),
      data: item.data ? stringify(item.data) : undefined
    };
  }

  protected async getAllFromSqlite(): Promise<WorkspaceRow[]> {
    const rows = await (this.sqliteModel as any).findAllRows();
    return rows.map((row: any) => this.normalizeRow(row));
  }

  protected async getFromSqliteById(id: string): Promise<WorkspaceRow | null> {
    const items = await (this.sqliteModel as any).findAllRows({ where: { id }, limit: 1 });
    return items.length > 0 ? this.normalizeRow(items[0]) : null;
  }

  private normalizeRow(row: any): WorkspaceRow {
    const toIso = (v: any): string => (typeof v === 'string' ? v : new Date(v).toISOString());
    return {
      id: row.id,
      name: row.name,
      createdAt: toIso(row.createdAt),
      updatedAt: toIso(row.updatedAt),
      deletedAt: row.deletedAt ? toIso(row.deletedAt) : undefined,
      data:
        row.data != null
          ? typeof row.data === 'string'
            ? row.data
            : stringify(row.data)
          : undefined
    };
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<WorkspaceCommonItem> {
    return {
      type: 'custom',
      getKey: (item) => `${item.name}-${item.userId}`
    };
  }

  /**
   * Override validateItemEquality to use workspace-specific validation
   * Since there's no specific validator in ValidationUtils, we use the base adapter's logic
   * but with workspace-specific logging and field handling
   */
  protected validateItemEquality(
    item1: WorkspaceCommonItem,
    item2: WorkspaceCommonItem
  ): { isEqual: boolean; differences: string[] } {
    // Use the base adapter's validation logic but with workspace-specific considerations
    const differences: string[] = [];

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(item1), ...Object.keys(item2)]);

    for (const key of allKeys) {
      const val1 = (item1 as any)[key];
      const val2 = (item2 as any)[key];

      if (key === 'createdAt' || key === 'updatedAt' || key === 'deletedAt') {
        // Handle date comparison with 1 second tolerance
        if (val1 && val2) {
          const time1 = val1 instanceof Date ? val1.getTime() : new Date(val1).getTime();
          const time2 = val2 instanceof Date ? val2.getTime() : new Date(val2).getTime();

          if (Math.abs(time1 - time2) > 1000) {
            differences.push(
              `Date field '${key}' differs by more than 1 second: ${val1} vs ${val2}`
            );
          }
        } else if (val1 !== val2) {
          differences.push(`Date field '${key}' differs: ${val1} vs ${val2}`);
        }
      } else if (key === 'data') {
        // Handle JSON data comparison with deep equality
        if (!this.deepEqual(val1, val2)) {
          differences.push(
            `JSON field '${key}' differs: ${JSON.stringify(val1)} vs ${JSON.stringify(val2)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (val1 !== val2) {
          differences.push(`Field '${key}' differs: ${val1} vs ${val2}`);
        }
      }
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }
}
